import { PrimaryButton } from "@digipost/dds-buttons-react";
import { Input } from "@digipost/dds-input-react";
import { useLanguage } from "@digipost/translate";
import { faExclamationCircle } from "@fortawesome/pro-solid-svg-icons/faExclamationCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import InfoWarningText from "../InfoWarningText";
import { Dispatch } from "../infrastructure/redux/actions";
import {
  organitionPendingAction,
  RegistrationStatus,
} from "../infrastructure/redux/org/org-actions";
import { ReduxState } from "../infrastructure/redux/state";
import Translated from "../infrastructure/Translated";
import { useTranslate } from "../infrastructure/useTranslate";
import { TrueFalseRadioButton } from "../registrer/TrueFalseRadioButton";
import { isValidFormValue } from "./validateForm";

export const ManuellSkjemaSignering: FC = () => {
  const [invalidInputs, setInvalidInputs] = useState<string[]>([]);

  const [organisationNumber, setOrganisationNumber] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [telephoneNumber, setTelephoneNumber] = useState<string>("");
  const [kommentar, setKommentar] = useState<string>("");
  const [activateMailbox, setActivateMailbox] = useState<boolean>(false);

  const { language } = useLanguage();

  const dispatch = useDispatch<Dispatch>();
  const translate = useTranslate();
  const validationRules = useSelector(
    (state: ReduxState) => state.app.validationRules,
  );
  const registrationStatus = useSelector(
    (state: ReduxState) => state.org.registrationStatus,
  );

  const firstName = useSelector(
    (state: ReduxState) => state.user.info?.firstName,
  );
  const lastName = useSelector(
    (state: ReduxState) => state.user.info?.lastName,
  );

  const userName = `${firstName} ${lastName}`;

  const validate = (): boolean => {
    const newInvalidInputs = [];

    if (
      !isValidFormValue(
        organisationNumber,
        true,
        validationRules,
        "organisationNumber",
      )
    ) {
      newInvalidInputs.push("organisationNumber");
    }

    if (!name?.length) {
      newInvalidInputs.push("name");
    }

    if (!isValidFormValue(email, true, validationRules, "email")) {
      newInvalidInputs.push("email");
    }

    if (
      !isValidFormValue(telephoneNumber, true, validationRules, "phoneNumber")
    ) {
      newInvalidInputs.push("telephoneNumber");
    }
    setInvalidInputs([...newInvalidInputs]);
    return newInvalidInputs.length === 0;
  };

  const submitManuellSkjema = (): void => {
    const valid = validate();
    if (valid) {
      dispatch(
        organitionPendingAction({
          organisationNumber: organisationNumber,
          email: email,
          telephoneNumber: telephoneNumber,
          language: language,
          activateMailbox: activateMailbox,
          name: name,
          integrationPartnerId: undefined,
          externalPartnerNonce: undefined,
          registrationContext: "signering",
          kommentar: kommentar,
        }),
      );
    }
  };
  const isFieldValid = (field: string): boolean => {
    return !invalidInputs.includes(field);
  };

  useEffect(() => {
    document.title = `Posten signering - ${translate(
      "register.org.intro.header",
    )}`;
  }, [translate]);

  return (
    <main>
      <div className="register-content-wrapper">
        <h1 className="hw-h1 hw-color-primary-darker">
          <Translated textKey="register.org.registration_form_signering.header" />
        </h1>
      </div>

      <div>
        <p>
          <Translated textKey="register.org.intro.signering" />
        </p>
        <div />
        <Input
          name="organisationNumber"
          valid={isFieldValid("organisationNumber")}
          errorIcon={<FontAwesomeIcon icon={faExclamationCircle} />}
          errorText={
            !isFieldValid("organisationNumber")
              ? translate("register.org.manual.form.org_nr.error")
              : undefined
          }
          onChange={(event) => setOrganisationNumber(event.target.value)}
          labelText={`${translate("generic.organisation_number")} *`}
          data-testid="manual-schema-organisation-number-input"
        />
        <Input
          name="name"
          valid={isFieldValid("name")}
          errorIcon={<FontAwesomeIcon icon={faExclamationCircle} />}
          errorText={
            !isFieldValid("name")
              ? translate("register.org.manual.form.org_name.error")
              : undefined
          }
          onChange={(event) => setName(event.target.value)}
          labelText={`${translate(
            "register.org.manual.form.org_name.label",
          )} *`}
          data-testid="manual-schema-organisation-name-input"
        />
        <Input
          name="username"
          labelText={`${translate("register.org.manual.form.name.label")} *`}
          disabled={true}
          value={userName}
          data-testid="manual-schema-user-name-input"
        />
        <Input
          name="email"
          valid={isFieldValid("email")}
          errorIcon={<FontAwesomeIcon icon={faExclamationCircle} />}
          errorText={
            !isFieldValid("email")
              ? translate("register.org.registration_form.invalid_email")
              : undefined
          }
          onChange={(event) => setEmail(event.target.value)}
          labelText={`${translate("register.your_email")} *`}
          data-testid="manual-schema-email-input"
        />
        <Input
          name="telephoneNumber"
          valid={isFieldValid("telephoneNumber")}
          errorIcon={<FontAwesomeIcon icon={faExclamationCircle} />}
          errorText={
            !isFieldValid("telephoneNumber")
              ? translate("register.org.registration_form.invalid_phone")
              : undefined
          }
          onChange={(event) => setTelephoneNumber(event.target.value)}
          inputType="phone"
          labelText={`${translate("register.your_phone_number")} *`}
          data-testid="manual-schema-phone-input"
        />
        <div className="dds-input">
          <label className="dds-bold" htmlFor="kommentar">
            <Translated textKey="register.org.manual.form.comment" />
          </label>
          <textarea
            id="kommentar"
            onChange={(event) => setKommentar(event.target.value)}
            name="kommentar"
            rows={3}
            data-testid="manual-schema-comment-textarea"
          />
        </div>
        <h2 className="dds-bold activate-mailbox__heading">
          <Translated textKey="register.org.signering_motta_digipost.header" />
        </h2>
        <p className="activate-mailbox__sub-heading">
          <Translated textKey="register.org.signering_motta_digipost.desc" />
        </p>
        <div className="extra-margin-bottom">
          <TrueFalseRadioButton
            name="activateMailbox"
            value={activateMailbox}
            change={() => setActivateMailbox(!activateMailbox)}
          />
        </div>
        {registrationStatus === RegistrationStatus.FAILED && (
          <InfoWarningText red={true} textkey="generic.error" />
        )}
        <div>
          <PrimaryButton
            id="til_godkjenning"
            onClick={submitManuellSkjema}
            data-testid="manual-schema-send-to-approval-button"
          >
            <Translated textKey="register.org.manual.form.buttonSubmit" />
          </PrimaryButton>
        </div>
      </div>
    </main>
  );
};
